import React, { useState, useEffect, useCallback } from 'react';
import { X, Inbox, Check, Ban, Send, Clock, ArrowRightLeft } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../services/api';
import { LockTransfer } from '../types';
import Modal from './ui/Modal';

interface TransfersInboxProps {
  onClose: () => void;
  /** Called after accepting/cancelling something that changes ownership, to refresh the locks. */
  onChanged: () => void;
}

const UnitChips: React.FC<{ transfer: LockTransfer }> = ({ transfer }) => (
  <div className="flex flex-wrap gap-1.5 mt-1">
    {transfer.units.map((u) => (
      <span
        key={u.id}
        className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
      >
        {u.name || u.id.slice(0, 8)}
      </span>
    ))}
  </div>
);

const TransfersInbox: React.FC<TransfersInboxProps> = ({ onClose, onChanged }) => {
  const [incoming, setIncoming] = useState<LockTransfer[]>([]);
  const [outgoing, setOutgoing] = useState<LockTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [inc, out] = await Promise.all([
      apiService.getIncomingTransfers(),
      apiService.getOutgoingTransfers(),
    ]);
    setIncoming(inc.data || []);
    setOutgoing(out.data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (
    id: string,
    action: (id: string) => Promise<{ success: boolean; message?: string; data?: any }>,
    onResult?: (data: any) => void
  ) => {
    setActioningId(id);
    setError(null);
    setNotice(null);
    const res = await action(id);
    setActioningId(null);
    if (res.success) {
      if (onResult) onResult(res.data);
      await load();
      onChanged(); // also refresh the parent badge (includes rejections)
    } else {
      setError(res.message || 'The operation failed.');
    }
  };

  const expiryLabel = (ts: number | null) =>
    ts ? `Expires on ${format(new Date(ts), 'dd/MM/yyyy')}` : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Lock transfers"
      icon={<ArrowRightLeft className="h-4 w-4 text-indigo-500" />}
      headerAccent="bg-indigo-50/50"
      maxWidth="2xl"
      bodyPadding={false}
    >
      {error && (
        <div className="mx-6 mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      {notice && (
        <div className="mx-6 mt-4 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
          {notice}
        </div>
      )}

      <div className="px-6 py-5 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-indigo-600" />
          </div>
        ) : (
          <>
            {/* Incoming */}
            <section>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center">
                <Inbox className="h-3.5 w-3.5 mr-1.5" />
                Incoming ({incoming.length})
              </h4>
              {incoming.length === 0 ? (
                <p className="text-sm text-slate-400 py-2">You have no transfers waiting to be accepted.</p>
              ) : (
                <div className="space-y-3">
                  {incoming.map((t) => (
                    <div key={t.id} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-slate-700">
                            From <span className="font-medium">{t.counterparty.email || t.counterparty.full_name || 'unknown account'}</span>
                          </p>
                          <UnitChips transfer={t} />
                          {expiryLabel(t.expires_at) && (
                            <p className="text-xs text-slate-400 mt-2 flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {expiryLabel(t.expires_at)}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <button
                            onClick={() =>
                              runAction(t.id, apiService.acceptTransfer.bind(apiService), (data) => {
                                if (data?.skipped_count > 0) {
                                  setNotice(
                                    `Transferred ${data.transferred_count}, ` +
                                    `skipped ${data.skipped_count} (destination already had ${data.skipped_count === 1 ? 'it' : 'them'}).`
                                  );
                                }
                              })
                            }
                            disabled={actioningId === t.id}
                            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-60"
                          >
                            <Check className="h-3.5 w-3.5 mr-1" />
                            Accept
                          </button>
                          <button
                            onClick={() => runAction(t.id, apiService.rejectTransfer.bind(apiService))}
                            disabled={actioningId === t.id}
                            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-60"
                          >
                            <Ban className="h-3.5 w-3.5 mr-1" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Outgoing */}
            <section>
              <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center">
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Sent ({outgoing.length})
              </h4>
              {outgoing.length === 0 ? (
                <p className="text-sm text-slate-400 py-2">You have no pending sent transfers.</p>
              ) : (
                <div className="space-y-3">
                  {outgoing.map((t) => (
                    <div key={t.id} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm text-slate-700">
                            To <span className="font-medium">{t.counterparty.email || t.counterparty.full_name || 'unknown account'}</span>
                          </p>
                          <UnitChips transfer={t} />
                          {expiryLabel(t.expires_at) && (
                            <p className="text-xs text-slate-400 mt-2 flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {expiryLabel(t.expires_at)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => runAction(t.id, apiService.cancelTransfer.bind(apiService))}
                          disabled={actioningId === t.id}
                          className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-60 flex-shrink-0"
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </Modal>
  );
};

export default TransfersInbox;
